import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'filepond/dist/filepond.min.css';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { EImageVariant } from '@onesource/schemas';
import type { ProcessServerConfigFunction } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FilePond as FilePondInstance } from 'react-filepond';
import { FilePond, registerPlugin } from 'react-filepond';

import { CropDialog } from './crop-dialog';
import { ALL_VARIANTS_NO_ORIGINAL, EMimeType, FILE_FORMATS, VARIANT_OPTIONS } from './file-uploader.constants';
import { useCropQueue } from './use-crop-queue';

export { EMimeType, FILE_FORMATS };

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
);

export type TFileUploaderProps = {
  /**
   * Explicit list of accepted MIME types.
   * Use FILE_FORMATS presets or build a custom list.
   * @example ['image/jpeg', 'image/png']
   * @example FILE_FORMATS.IMAGE_AND_PDF
   */
  allowedFormats: EMimeType[];
  /** Maximum file size. Defaults to '50MB'. */
  maxFileSize?: string;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Called with the server ID (object key) after each successful upload */
  onUploaded?: (key: string, filename: string) => void;
  /**
   * Factory that builds a FilePond process handler for the given enabled variants.
   * Preferred over `processHandler` when image variant selection is needed.
   */
  processHandlerFactory?: (enabledVariants: EImageVariant[]) => ProcessServerConfigFunction;
  /** FilePond server process handler — used when processHandlerFactory is not provided */
  processHandler?: ProcessServerConfigFunction;
  /** Label shown in the drop area */
  label?: string;
  /** Override the minimum height of the drop zone (e.g. '240px'). Defaults to FilePond's built-in height. */
  dropzoneHeight?: string;
  /**
   * Initial set of enabled image variants. Defaults to all variants except original.
   * Only relevant when `processHandlerFactory` is provided and `allowedFormats` includes images.
   */
  imageVariants?: EImageVariant[];
  /**
   * When true the variant checkboxes are interactive (admin mode).
   * When false (default) they are rendered as read-only indicators.
   */
  variantsEditable?: boolean;
  /**
   * Aspect ratio for the crop tool (width / height). e.g. 1 for square, 16/9 for widescreen.
   * Defaults to free-form (NaN).
   */
  cropAspectRatio?: number;
  /** Number of file thumbnails to show per row. Defaults to 1. */
  uploaderColumns?: number;
};

export function FileUploader({
  allowedFormats,
  maxFileSize = '50MB',
  maxFiles = 1,
  onUploaded,
  processHandlerFactory,
  processHandler: processHandlerProp,
  label,
  dropzoneHeight,
  imageVariants = ALL_VARIANTS_NO_ORIGINAL,
  variantsEditable = false,
  cropAspectRatio = NaN,
  uploaderColumns = 1,
}: TFileUploaderProps) {
  const acceptsImages = allowedFormats.some(f => f.startsWith('image/'));
  const showVariants = acceptsImages && processHandlerFactory !== null;
  const theme = useTheme();
  const uid = useId().replace(/:/g, '');
  const scopeClass = `fp-${uid}`;

  const [enabledVariants, setEnabledVariants] = useState<EImageVariant[]>(imageVariants);

  const pondRef = useRef<FilePondInstance | null>(null);

  const {
    cropOpen,
    cropSrc,
    handleCropConfirm,
    handleCropSkip,
    handleCropCancel,
    handleDialogExited,
    handleProcessFile,
    onAddFile,
    onRemoveFile,
  } = useCropQueue({ cropAspectRatio, pondRef, onUploaded });

  // Keep a ref to enabledVariants so the process handler can read the latest value
  const enabledVariantsRef = useRef<EImageVariant[]>(enabledVariants);
  useEffect(() => {
    enabledVariantsRef.current = enabledVariants;
  }, [enabledVariants]);

  const processHandler = useMemo(
    () => (processHandlerFactory ? processHandlerFactory(enabledVariants) : processHandlerProp!),
    // Re-build handler whenever enabled variants change (only when factory is provided)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processHandlerFactory, processHandlerProp, enabledVariants],
  );

  const handleVariantToggle = (variant: EImageVariant) => {
    setEnabledVariants(prev => {
      if (prev.includes(variant)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter(v => v !== variant);
      }
      return [...prev, variant];
    });
  };

  const primaryMain = theme.palette.primary.main;

  const defaultLabel = (() => {
    const hasImages = allowedFormats.some(f => f.startsWith('image/'));
    const hasPdf = allowedFormats.includes(EMimeType.PDF);
    if (hasImages && hasPdf) {
      return 'images or PDFs';
    }
    if (hasImages) {
      return 'images';
    }
    if (hasPdf) {
      return 'PDFs';
    }
    return 'files';
  })();

  return (
    <>
      {showVariants && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Generate sizes
          </Typography>
          <FormGroup row>
            {VARIANT_OPTIONS.map(({ variant, label: variantLabel }) => (
              <FormControlLabel
                key={variant}
                control={
                  <Checkbox
                    size="small"
                    checked={enabledVariants.includes(variant)}
                    onChange={() => handleVariantToggle(variant)}
                    disabled={!variantsEditable}
                  />
                }
                label={<Typography variant="caption">{variantLabel}</Typography>}
                sx={{ mr: 1 }}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      <style>{`
        .${scopeClass} .filepond--root {
          border: 2px dashed ${primaryMain};
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.2s, background-color 0.2s;
          ${dropzoneHeight ? `min-height: ${dropzoneHeight};` : ''}
        }
        .${scopeClass} .filepond--root:hover {
          border-color: ${theme.palette.primary.dark};
        }
        .${scopeClass} .filepond--panel-root {
          background-color: ${primaryMain}12;
          border: none;
          box-shadow: none;
        }
        ${
          dropzoneHeight
            ? `
        .${scopeClass} .filepond--drop-label {
          min-height: ${dropzoneHeight};
        }
        `
            : ''
        }
        .${scopeClass} .filepond--drop-label label {
          color: ${theme.palette.primary.dark};
          font-size: 1.3rem;
          font-weight: 500;
        }
        .${scopeClass} .filepond--label-action {
          color: ${primaryMain};
          text-decoration-color: ${primaryMain};
          font-weight: 700;
          font-size: 1.3rem;
        }
        .${scopeClass} .filepond--list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px;
          position: static !important;
          left: unset !important;
          right: unset !important;
          width: 100% !important;
        }
        .${scopeClass} .filepond--item {
          position: static !important;
          transform: none !important;
          width: calc(${100 / uploaderColumns}% - ${((uploaderColumns - 1) * 8) / uploaderColumns}px) !important;
          margin: 0 !important;
        }
        .${scopeClass} .filepond--image-preview-wrapper {
          height: 120px !important;
        }
        .${scopeClass} .filepond--image-preview {
          height: 120px !important;
        }
      `}</style>
      <div className={scopeClass}>
        <FilePond
          ref={pondRef}
          allowMultiple={maxFiles > 1}
          maxFiles={maxFiles}
          acceptedFileTypes={allowedFormats as string[]}
          labelFileTypeNotAllowed={`Only ${defaultLabel} are allowed`}
          maxFileSize={maxFileSize}
          labelMaxFileSizeExceeded={`File is too large (max ${maxFileSize})`}
          styleButtonRemoveItemPosition="left"
          imagePreviewHeight={120}
          labelIdle={label ?? `Drag & drop your ${defaultLabel} or <span class="filepond--label-action">Browse</span>`}
          instantUpload={!acceptsImages}
          onaddfile={(_err, file) => {
            const isPdf = (file.file as File).type === 'application/pdf';
            if (!acceptsImages || isPdf) {
              return;
            }
            onAddFile(file.file as File, file.id);
          }}
          server={{ process: processHandler }}
          onprocessfile={handleProcessFile}
          onremovefile={(_err, file) => {
            onRemoveFile(file.serverId as string | null, file.id);
          }}
          credits={false}
        />
      </div>

      {acceptsImages && (
        <CropDialog
          open={cropOpen}
          src={cropSrc}
          cropAspectRatio={cropAspectRatio}
          onConfirm={handleCropConfirm}
          onSkip={handleCropSkip}
          onCancel={handleCropCancel}
          onExited={handleDialogExited}
        />
      )}
    </>
  );
}
